import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { enqueueGeneration, selectCreativeJobsMap } from '@/store/slices/creativeJobs';
import { creditCostFor } from '@/store/slices/aiCredits';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { Button, Icon } from '../../../../components';
import { GenerateButton } from '../GenerateButton';
import { CREATIVE_SWATCHES, CREATIVE_GRADIENT_STOPS, CREATIVE_BRAND_FALLBACK } from '../../../../theme/tokens';
import {
  CREATIVE_FORMATS,
  FORMAT_GROUPS,
  gradientCss,
  normalizeDraft,
  makeExtra,
  DEFAULT_LOGO_SCALE,
  DEFAULT_HEADLINE_W,
  DEFAULT_SUB_W,
  DEFAULT_CTA_W,
  DEFAULT_CTA_H,
  type CreativeFormat,
  type CreativeDraft,
  type Selection,
  type ExtraKind,
  type TextAlign,
  type TextSize,
} from '../../custom-formats';
import { listCustom, saveCustom, deleteCustom, customApiConfigured, type SavedCustom } from '../../custom-api';
import { renderCreativeToCanvas } from './renderCreative';
import { EditorStage } from './EditorStage';
import * as S from './CustomStudio.styles';
import { MASCOT_AUTO, type MascotPick } from '../../../art-style/art-style-api';
import { MascotPicker } from '../../../art-style/components/MascotPicker';

export type CustomStudioProps = {
  brandName: string;
  brandLogoUrl?: string;
  tenantId?: number;
  /** Open Buy-credits when a generation can't be afforded. */
  onBuyCredits: () => void;
  /** Surface a short status message (toast) to the owner. */
  onNotify: (message: string) => void;
  /** The brand's existing banner image URLs, offered as backgrounds to pick from. */
  brandBanners?: string[];
  /** The brand's logo-family URLs, offered in the canvas "+" menu. */
  brandLogos?: string[];
  /**
   * Fires true while the editor/picker is open (not the saved list). The Creatives page hides its
   * tab bar then, so the local editor header is the top-level drill-in element.
   */
  onImmerseChange?: (immersed: boolean) => void;
};

/** Read an uploaded file as a data URI (kept same-origin so the export canvas doesn't taint). */
const readAsDataUri = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsDataURL(file);
  });

const WHITE = CREATIVE_SWATCHES[0];
const SIZES: TextSize[] = ['sm', 'md', 'lg'];
const ALIGNS: TextAlign[] = ['left', 'center', 'right'];

const gradientById = (id: string): { angle: number; stops: readonly string[] } =>
  CREATIVE_GRADIENT_STOPS.find((g) => g.id === id) ?? CREATIVE_GRADIENT_STOPS[0];

/** A fresh centred composition on the brand's default gradient. */
const makeDraft = (brandColor: string): CreativeDraft => ({
  headline: {
    text: 'Your headline here',
    color: WHITE,
    size: 'lg',
    align: 'center',
    pos: { x: 0.5, y: 0.58 },
    width: DEFAULT_HEADLINE_W,
    scale: 1,
    rot: 0,
  },
  subheadline: {
    text: 'A short supporting line',
    color: WHITE,
    size: 'sm',
    align: 'center',
    pos: { x: 0.5, y: 0.72 },
    width: DEFAULT_SUB_W,
    scale: 1,
    rot: 0,
  },
  cta: {
    text: 'Play now',
    color: brandColor,
    show: true,
    pos: { x: 0.5, y: 0.85 },
    width: DEFAULT_CTA_W,
    height: DEFAULT_CTA_H,
    scale: 1,
    rot: 0,
  },
  logo: { show: true, pos: { x: 0.5, y: 0.1 }, scale: DEFAULT_LOGO_SCALE, rot: 0 },
  background: { kind: 'gradient', value: CREATIVE_GRADIENT_STOPS[0].id, scrim: 0.12, offset: { x: 0, y: 0 } },
  extras: [],
});

const bgPreview = (d: CreativeDraft): { bg: string; image?: string } =>
  d.background.kind === 'image' && d.background.imageUrl
    ? { bg: '', image: d.background.imageUrl }
    : d.background.kind === 'color'
      ? { bg: d.background.value }
      : { bg: gradientCss(gradientById(d.background.value)) };

/**
 * Custom studio — a brand-aware creative editor. The owner picks a format, then composes on a live,
 * directly-manipulable canvas ({@link EditorStage}): a background (colour / gradient / an AI image
 * Lumio generates), a headline, a subheadline, a CTA and the logo, each dragged into place and
 * edited inline. The right panel is contextual to the selection. Work AUTOSAVES (no Save button);
 * the result downloads as a PNG. Credits are spent only on the AI background.
 */
export const CustomStudio = ({
  brandName,
  brandLogoUrl,
  tenantId,
  onBuyCredits,
  onNotify,
  brandBanners = [],
  brandLogos = [],
  onImmerseChange,
}: CustomStudioProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const brandColor = CREATIVE_BRAND_FALLBACK;

  const [view, setView] = React.useState<'list' | 'edit'>('list');
  const [saved, setSaved] = React.useState<SavedCustom[]>([]);
  const [editingId, setEditingId] = React.useState<string | undefined>(undefined);
  const [name, setName] = React.useState('');
  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved'>('idle');

  const [format, setFormat] = React.useState<CreativeFormat | null>(null);
  const [draft, setDraft] = React.useState<CreativeDraft>(() => makeDraft(brandColor));
  const [selected, setSelected] = React.useState<Selection>('background');
  const [prompt, setPrompt] = React.useState('');
  // Which of the brand's mascots the background draws (Auto by default; the picker hides without a cast).
  const [mascotPick, setMascotPick] = React.useState<MascotPick>(MASCOT_AUTO);
  const [bgJobId, setBgJobId] = React.useState<string | undefined>(undefined);

  const update = React.useCallback((patch: (d: CreativeDraft) => CreativeDraft): void => setDraft(patch), []);

  // Set the background to a specific image (an upload data-URI, or one of the brand's banners).
  const setBgImage = React.useCallback(
    (url: string): void =>
      update((d) => ({
        ...d,
        background: {
          kind: 'image',
          value: d.background.value,
          imageUrl: url,
          scrim: Math.max(d.background.scrim, 0.18),
          offset: { x: 0, y: 0 },
        },
      })),
    [update],
  );
  const onUploadBg = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = event.currentTarget.files?.[0];
      event.currentTarget.value = '';
      if (!file) return;
      try {
        setBgImage(await readAsDataUri(file));
      } catch {
        onNotify(t("That image couldn't be loaded. Please try another."));
      }
    },
    [setBgImage, onNotify, t],
  );

  // ── Extra elements (logos / stickers added from the canvas "+" menu) ──
  const extraSeq = React.useRef(0);
  const addExtra = React.useCallback(
    (kind: ExtraKind, src: string): void => {
      extraSeq.current += 1;
      const id = `x-${Date.now().toString(36)}-${extraSeq.current}`;
      const el = makeExtra(kind, src, id);
      update((d) => ({ ...d, extras: [...d.extras, el] }));
      setSelected({ extra: id });
    },
    [update],
  );
  const removeExtra = React.useCallback(
    (id: string): void => {
      update((d) => ({ ...d, extras: d.extras.filter((e) => e.id !== id) }));
      setSelected('background');
    },
    [update],
  );
  const setExtraScale = React.useCallback(
    (id: string, s: number): void =>
      update((d) => ({ ...d, extras: d.extras.map((e) => (e.id === id ? { ...e, scale: s } : e)) })),
    [update],
  );
  const bringExtraToFront = React.useCallback(
    (id: string): void =>
      update((d) => {
        const ex = d.extras.find((e) => e.id === id);
        return ex ? { ...d, extras: [...d.extras.filter((e) => e.id !== id), ex] } : d;
      }),
    [update],
  );

  // Tell the page to hide its tab bar while the editor/picker is open (so BackHeader tops the view).
  React.useEffect(() => {
    onImmerseChange?.(view === 'edit');
    return () => onImmerseChange?.(false);
  }, [view, onImmerseChange]);

  // Load the owner's saved custom drafts.
  const refreshSaved = React.useCallback((signal?: AbortSignal): void => {
    if (!customApiConfigured()) return;
    listCustom(signal)
      .then((list) => {
        if (!signal?.aborted) setSaved(list);
      })
      .catch(() => {
        if (!signal?.aborted) setSaved([]);
      });
  }, []);
  React.useEffect(() => {
    const controller = new AbortController();
    refreshSaved(controller.signal);
    return () => controller.abort();
  }, [refreshSaved]);

  // ── Autosave ────────────────────────────────────────────────────────────────
  // No manual Save: the editor persists on its own, debounced, defaulting the name to "Untitled".
  const persist = React.useCallback(async (): Promise<void> => {
    if (!format) return;
    const finalName = name.trim() || draft.headline.text.trim() || t('Untitled creative');
    try {
      const id = await saveCustom({ id: editingId, tenantId, name: finalName, formatId: format.id, draft });
      setEditingId((prev) => prev ?? id);
      setSaveState('saved');
    } catch {
      // Silent: a transient failure just leaves the last change unsaved; the next edit retries.
      setSaveState('idle');
    }
  }, [format, name, draft, editingId, tenantId, t]);
  const persistRef = React.useRef(persist);
  persistRef.current = persist;

  React.useEffect(() => {
    if (view !== 'edit' || !format) return undefined;
    setSaveState('saving');
    const timer = window.setTimeout(() => void persistRef.current(), 800);
    return () => window.clearTimeout(timer);
  }, [draft, name, format, view]);

  const startNew = React.useCallback((): void => {
    setEditingId(undefined);
    setName('');
    setFormat(null);
    setDraft(makeDraft(brandColor));
    setSelected('background');
    setPrompt('');
    setSaveState('idle');
    setView('edit');
  }, [brandColor]);

  const openSaved = React.useCallback((item: SavedCustom): void => {
    setEditingId(item.id);
    setName(item.name);
    setFormat(CREATIVE_FORMATS.find((f) => f.id === item.format_id) ?? null);
    setDraft(normalizeDraft(item.draft));
    setSelected('background');
    setPrompt('');
    setSaveState('saved');
    setView('edit');
  }, []);

  const backToList = React.useCallback((): void => {
    if (view === 'edit' && format) void persistRef.current();
    setView('list');
    setSelected('background');
    refreshSaved();
  }, [view, format, refreshSaved]);

  const remove = React.useCallback(
    async (id: string): Promise<void> => {
      try {
        await deleteCustom(id);
        setSaved((prev) => prev.filter((s) => s.id !== id));
      } catch {
        onNotify(t("That couldn't be deleted. Please try again."));
      }
    },
    [onNotify, t],
  );

  // Watch the async AI-background job: drop the image in when it lands.
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const customCost = useAppSelector((s) => creditCostFor(s.aiCredits?.pricing, 'custom'));
  React.useEffect(() => {
    if (!bgJobId) return;
    const job = jobsById[bgJobId];
    if (!job) return;
    if (job.status === 'done' && job.result_url) {
      const url = job.result_url;
      update((d) => ({
        ...d,
        background: {
          kind: 'image',
          value: d.background.value,
          imageUrl: url,
          scrim: Math.max(d.background.scrim, 0.18),
          offset: { x: 0, y: 0 },
        },
      }));
      setBgJobId(undefined);
    } else if (job.status === 'failed') {
      setBgJobId(undefined);
      onNotify(t('That background generation failed. Please try again.'));
    }
  }, [jobsById, bgJobId, update, onNotify, t]);

  const generating = bgJobId !== undefined;

  const generateBackground = React.useCallback(async (): Promise<void> => {
    if (!tenantId || !prompt.trim() || !format) return;
    const brief = `${prompt.trim()}. A marketing background image for ${brandName || 'an online casino'}, ${format.name} format, cinematic and premium, on-brand, NO text or logos.`;
    try {
      const res = await dispatch(
        enqueueGeneration({ tenantId, placement: 'custom-studio', prompt: brief, mascotId: mascotPick || undefined }),
      ).unwrap();
      if (!res.ok) {
        if (res.needed !== undefined) onBuyCredits();
        else onNotify(t("That couldn't be started. Please try again."));
        return;
      }
      setBgJobId(res.jobId);
      onNotify(t("Generating a background. We'll drop it in when it's ready."));
      void dispatch(fetchOwnerWallets());
    } catch {
      onNotify(t("That couldn't be started. Please try again."));
    }
  }, [tenantId, prompt, format, brandName, mascotPick, dispatch, onBuyCredits, onNotify, t]);

  const download = React.useCallback(async (): Promise<void> => {
    if (!format) return;
    try {
      const canvas = await renderCreativeToCanvas(draft, format, draft.logo.show ? brandLogoUrl : undefined);
      canvas.toBlob((blob) => {
        if (!blob) {
          onNotify(t("That couldn't be exported. Please try again."));
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${brandName || 'creative'}-${format.id}.png`.toLowerCase().replace(/\s+/g, '-');
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch {
      onNotify(t('The background image blocks download for now. Try a colour or gradient background to export.'));
    }
  }, [draft, format, brandLogoUrl, brandName, onNotify, t]);

  /* ── Saved list (My creatives) ─────────────────────────────────────────────── */
  if (view === 'list') {
    return (
      <div>
        <S.PickerIntro>
          <S.PickerTitle text="Your custom creatives" />
          <S.PickerBody text="Standalone assets for social, ads, or anywhere off your site. Create a new one, or open a saved creative to edit or download it." />
        </S.PickerIntro>
        <S.SavedGrid>
          <S.NewTile type="button" onClick={startNew} aria-label={t('Create a new creative')}>
            <Icon name="plus-circle" size={26} />
            <S.NewTileText text="Create new" />
          </S.NewTile>
          {saved.map((item) => {
            const f = CREATIVE_FORMATS.find((x) => x.id === item.format_id);
            const preview = bgPreview(item.draft);
            return (
              <S.SavedCard key={item.id}>
                <S.SavedPreview
                  type="button"
                  $bg={preview.bg}
                  $image={preview.image}
                  onClick={() => openSaved(item)}
                  aria-label={t('Open {{name}}', { name: item.name || 'creative' })}
                />
                <S.SavedBody>
                  <S.SavedInfo>
                    <S.SavedName text={item.name || t('Untitled creative')} />
                    <S.SavedMeta text={f ? f.name : item.format_id} />
                  </S.SavedInfo>
                  <S.SavedActions>
                    <S.SavedBtn type="button" onClick={() => openSaved(item)} aria-label={t('Edit')}>
                      <Icon name="edit" size={14} />
                    </S.SavedBtn>
                    <S.SavedBtn type="button" onClick={() => void remove(item.id)} aria-label={t('Delete')}>
                      <Icon name="trash" size={14} />
                    </S.SavedBtn>
                  </S.SavedActions>
                </S.SavedBody>
              </S.SavedCard>
            );
          })}
        </S.SavedGrid>
      </div>
    );
  }

  /* ── Format picker (Create new) ────────────────────────────────────────────── */
  if (!format) {
    return (
      <div>
        <S.EditorHeader>
          <S.EditorBack type="button" onClick={backToList} aria-label={t('Back')}>
            <Icon name="arrow-left" size={18} />
          </S.EditorBack>
          <S.EditorTitle text="New creative" />
        </S.EditorHeader>
        <S.PickerIntro>
          <S.PickerTitle text="Pick a size to start" />
          <S.PickerBody text="Design an on-brand image for social, ads, or anywhere off your site. Pick a size, then compose it and generate a background with Lumio." />
        </S.PickerIntro>
        {FORMAT_GROUPS.map((group) => (
          <S.Group key={group}>
            <S.GroupTitle text={group} />
            <S.FormatGrid>
              {CREATIVE_FORMATS.filter((f) => f.group === group).map((f) => (
                <S.FormatTile
                  key={f.id}
                  type="button"
                  onClick={() => setFormat(f)}
                  aria-label={t('{{name}} format', { name: f.name })}
                >
                  <S.FormatShapeFrame>
                    <S.FormatShape $ratio={f.w / f.h} />
                  </S.FormatShapeFrame>
                  <S.FormatName text={f.name} />
                  <S.FormatDim text={`${f.w} × ${f.h}`} />
                </S.FormatTile>
              ))}
            </S.FormatGrid>
          </S.Group>
        ))}
      </div>
    );
  }

  /* ── Editor ────────────────────────────────────────────────────────────────── */
  const setLayer = (
    kind: 'headline' | 'subheadline',
    patch: Partial<{ text: string; color: string; size: TextSize; align: TextAlign }>,
  ): void =>
    update((d) =>
      kind === 'headline'
        ? { ...d, headline: { ...d.headline, ...patch } }
        : { ...d, subheadline: { ...d.subheadline, ...patch } },
    );

  const textPanel = (kind: 'headline' | 'subheadline', label: string): React.ReactElement => {
    const layer = draft[kind];
    return (
      <S.Section>
        <S.SectionLabel text={label} />
        <S.FieldGap>
          <S.Field>
            <S.FieldTitle text="Text" />
            <S.Input
              value={layer.text}
              onChange={(e) => setLayer(kind, { text: e.currentTarget.value })}
              placeholder={t(label)}
              aria-label={t(label)}
            />
          </S.Field>
          <S.Field>
            <S.FieldTitle text="Size" />
            <S.SegRowFull>
              {SIZES.map((s) => (
                <S.SegBtn key={s} type="button" $active={layer.size === s} onClick={() => setLayer(kind, { size: s })}>
                  {s === 'sm' ? t('Small') : s === 'md' ? t('Medium') : t('Large')}
                </S.SegBtn>
              ))}
            </S.SegRowFull>
          </S.Field>
          <S.Field>
            <S.FieldTitle text="Alignment" />
            <S.SegRowFull>
              {ALIGNS.map((a) => (
                <S.SegBtn
                  key={a}
                  type="button"
                  $active={layer.align === a}
                  onClick={() => setLayer(kind, { align: a })}
                  aria-label={t('Align {{a}}', { a })}
                >
                  {a === 'left' ? t('Left') : a === 'center' ? t('Center') : t('Right')}
                </S.SegBtn>
              ))}
            </S.SegRowFull>
          </S.Field>
          <S.Field>
            <S.FieldTitle text="Colour" />
            <S.SwatchRow>
              {CREATIVE_SWATCHES.slice(0, 8).map((c) => (
                <S.Swatch
                  key={c}
                  type="button"
                  $color={c}
                  $selected={layer.color === c}
                  onClick={() => setLayer(kind, { color: c })}
                  aria-label={t('Text colour')}
                />
              ))}
            </S.SwatchRow>
          </S.Field>
        </S.FieldGap>
      </S.Section>
    );
  };

  const ctaPanel = (): React.ReactElement => (
    <S.Section>
      <S.RowBetween>
        <S.SectionLabel text="Call to action" />
        <S.Toggle
          type="button"
          $on={draft.cta.show}
          onClick={() => update((d) => ({ ...d, cta: { ...d.cta, show: !d.cta.show } }))}
          aria-pressed={draft.cta.show}
          aria-label={t('Show call to action')}
        />
      </S.RowBetween>
      {draft.cta.show ? (
        <S.FieldGap>
          <S.Field>
            <S.FieldTitle text="Label" />
            <S.Input
              value={draft.cta.text}
              onChange={(e) => update((d) => ({ ...d, cta: { ...d.cta, text: e.currentTarget.value } }))}
              placeholder={t('Button label')}
              aria-label={t('Button label')}
            />
          </S.Field>
          <S.Field>
            <S.FieldTitle text="Colour" />
            <S.SwatchRow>
              {CREATIVE_SWATCHES.map((c) => (
                <S.Swatch
                  key={c}
                  type="button"
                  $color={c}
                  $selected={draft.cta.color === c}
                  onClick={() => update((d) => ({ ...d, cta: { ...d.cta, color: c } }))}
                  aria-label={t('Button colour')}
                />
              ))}
            </S.SwatchRow>
          </S.Field>
        </S.FieldGap>
      ) : (
        <S.HintText text="Turn the button on to add a call to action." />
      )}
    </S.Section>
  );

  const logoPanel = (): React.ReactElement => (
    <S.Section>
      <S.RowBetween>
        <S.SectionLabel text="Logo" />
        <S.Toggle
          type="button"
          $on={draft.logo.show}
          onClick={() => update((d) => ({ ...d, logo: { ...d.logo, show: !d.logo.show } }))}
          aria-pressed={draft.logo.show}
          aria-label={t('Show logo')}
        />
      </S.RowBetween>
      {draft.logo.show ? (
        brandLogoUrl ? (
          <S.FieldGap>
            <S.MiniLabel text="Size" />
            <S.Slider
              type="range"
              min={6}
              max={30}
              value={Math.round(draft.logo.scale * 100)}
              onChange={(e) =>
                update((d) => ({ ...d, logo: { ...d.logo, scale: Number(e.currentTarget.value) / 100 } }))
              }
              aria-label={t('Logo size')}
            />
            <S.HintText text="Drag the logo on the canvas to place it." />
          </S.FieldGap>
        ) : (
          <S.HintText text="No brand logo on file for this brand yet." />
        )
      ) : null}
    </S.Section>
  );

  const backgroundPanel = (): React.ReactElement => (
    <S.Section>
      <S.SectionLabel text="Background" />
      <S.FieldGap>
        <S.GradientRow>
          {CREATIVE_GRADIENT_STOPS.map((g) => (
            <S.GradientSwatch
              key={g.id}
              type="button"
              $bg={gradientCss(g)}
              $selected={draft.background.kind === 'gradient' && draft.background.value === g.id}
              onClick={() =>
                update((d) => ({
                  ...d,
                  background: { kind: 'gradient', value: g.id, scrim: 0, offset: { x: 0, y: 0 } },
                }))
              }
              aria-label={t('Gradient {{name}}', { name: g.id })}
            />
          ))}
        </S.GradientRow>
        <S.SwatchRow>
          {CREATIVE_SWATCHES.map((c) => (
            <S.Swatch
              key={c}
              type="button"
              $color={c}
              $selected={draft.background.kind === 'color' && draft.background.value === c}
              onClick={() =>
                update((d) => ({ ...d, background: { kind: 'color', value: c, scrim: 0, offset: { x: 0, y: 0 } } }))
              }
              aria-label={t('Solid colour')}
            />
          ))}
        </S.SwatchRow>
        <S.Field>
          <S.FieldTitle text="Your images" />
          <S.UploadBtn>
            <Icon name="plus" size={14} />
            <span>{t('Upload an image')}</span>
            <input type="file" accept="image/*" onChange={(e) => void onUploadBg(e)} />
          </S.UploadBtn>
          {brandBanners.length > 0 ? (
            <S.ImageRow>
              {brandBanners.map((url) => (
                <S.ImageThumb
                  key={url}
                  type="button"
                  $image={url}
                  $selected={draft.background.kind === 'image' && draft.background.imageUrl === url}
                  onClick={() => setBgImage(url)}
                  aria-label={t('Use this banner as the background')}
                />
              ))}
            </S.ImageRow>
          ) : null}
        </S.Field>
        <S.ArtStyleRow>
          <S.StudioArtStyleChip />
          <MascotPicker value={mascotPick} onChange={setMascotPick} disabled={generating} />
        </S.ArtStyleRow>
        <S.Prompt
          value={prompt}
          onChange={(e) => setPrompt(e.currentTarget.value)}
          placeholder={t('Describe a background for Lumio to generate, e.g. neon city skyline at night')}
          aria-label={t('Describe a background')}
        />
        <GenerateButton
          label={generating ? t('Generating…') : t('Generate with Lumio')}
          cost={customCost}
          disabled={generating || prompt.trim() === '' || !tenantId}
          onClick={() => void generateBackground()}
        />
        {draft.background.kind === 'image' ? (
          <S.RowBetween>
            <S.MiniLabel text="Darken for text" />
            <S.SegRow>
              {[0, 0.25, 0.45].map((a) => (
                <S.SegBtn
                  key={a}
                  type="button"
                  $active={Math.abs(draft.background.scrim - a) < 0.01}
                  onClick={() => update((d) => ({ ...d, background: { ...d.background, scrim: a } }))}
                >
                  {a === 0 ? t('None') : a === 0.25 ? t('Soft') : t('Strong')}
                </S.SegBtn>
              ))}
            </S.SegRow>
          </S.RowBetween>
        ) : null}
      </S.FieldGap>
    </S.Section>
  );

  const extraPanel = (id: string): React.ReactElement => {
    const ex = draft.extras.find((e) => e.id === id);
    if (!ex) return backgroundPanel();
    return (
      <S.Section>
        <S.SectionLabel text={ex.kind === 'logo' ? 'Logo' : 'Sticker'} />
        <S.FieldGap>
          <S.Field>
            <S.FieldTitle text="Size" />
            <S.Slider
              type="range"
              min={4}
              max={90}
              value={Math.round(ex.scale * 100)}
              onChange={(e) => setExtraScale(id, Number(e.currentTarget.value) / 100)}
              aria-label={t('Size')}
            />
          </S.Field>
          <S.RowBetween>
            <Button variant="outline" label={t('Bring to front')} onClick={() => bringExtraToFront(id)} />
            <Button variant="text" icon="trash" label={t('Delete')} onClick={() => removeExtra(id)} />
          </S.RowBetween>
          <S.HintText text="Drag it on the canvas to move, corners to resize, the top handle to rotate." />
        </S.FieldGap>
      </S.Section>
    );
  };

  const contextPanel = (): React.ReactElement => {
    if (selected && typeof selected === 'object' && 'extra' in selected) return extraPanel(selected.extra);
    switch (selected) {
      case 'headline':
        return textPanel('headline', 'Headline');
      case 'subheadline':
        return textPanel('subheadline', 'Subheadline');
      case 'cta':
        return ctaPanel();
      case 'logo':
        return logoPanel();
      default:
        return backgroundPanel();
    }
  };

  return (
    <div>
      <S.EditorHeader>
        <S.EditorBack type="button" onClick={backToList} aria-label={t('Back')}>
          <Icon name="arrow-left" size={18} />
        </S.EditorBack>
        <S.EditorTitle text={name.trim() || 'Untitled creative'} />
        <S.ToolbarActions>
          {saveState !== 'idle' ? <S.SaveStatus text={saveState === 'saving' ? 'Saving…' : 'Saved'} /> : null}
          <Button variant="primary" icon="download" label={t('Download')} onClick={() => void download()} />
        </S.ToolbarActions>
      </S.EditorHeader>

      <S.StudioGrid>
        <EditorStage
          format={format}
          draft={draft}
          logoUrl={brandLogoUrl}
          brandLogos={brandLogos}
          selected={selected}
          onSelect={setSelected}
          onChange={update}
          onAddExtra={addExtra}
        />

        <S.Panel>
          {/* Canvas-level, always available: name + format + a hint. */}
          <S.Section>
            <S.FieldGap>
              <S.Field>
                <S.FieldTitle text="Name" />
                <S.Input
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  placeholder={t('Untitled creative')}
                  aria-label={t('Creative name')}
                />
              </S.Field>
              <S.MiniLabel text={`${format.name} · ${format.w} × ${format.h}`} />
              <S.HintText text="Click an element to edit it. Drag to move, double-click text to type, corners to resize, top handle to rotate." />
            </S.FieldGap>
          </S.Section>

          {contextPanel()}
        </S.Panel>
      </S.StudioGrid>
    </div>
  );
};

export default CustomStudio;
